/**
 * @module checkPermissions
 * @description Centralizes permission and role-based functionality for RIS clients
 *
 * This module provides a comprehensive solution for managing restricted access
 * for external RIS users who have limited permissions compared to standard users.
 * It returns boolean flags that can be used throughout the application to control
 * feature access and UI rendering based on user permissions.
 */

import { useCurrentUser } from 'hooks/useCurrentUser';
import { useIsAuthenticatedAsExternalUser } from 'hooks/useAuth';

// Permission and role constants to prevent typos and improve maintainability
enum ROLES {
  RIS = 'ExternalIntegrationUser',
}

/**
 * RIS User Permission Restrictions
 *
 * RIS users have several restrictions across the application:
 *
 * UI/Navigation Restrictions:
 * - Left sidebar is not rendered
 * - Cannot access Viewer Settings and Personal Information
 * - Cannot open Viewer 1 UI, Worklist UI, or Reporter UI
 * - User should have various UI enhancements like the Sirona Logo moved to the top left corner of the viewport
 *
 * Feature Restrictions:
 * - Shortcuts are read-only
 * - Cannot see priors outside of case's MRN
 * - Cannot edit mouse settings (view only)
 * - Cannot create hanging protocols, presentation states, or annotations
 * - Cannot move case to QC or claim case
 * - Tool changes in toolbar are not persisted
 */

/**
 * Hook that checks and returns all permission flags for RIS users
 *
 * @returns {Object} Object containing boolean flags for various permissions
 */
export const useCheckUserRISPermissions = () => {
  const { data } = useCurrentUser();
  const isAuthenticatedAsExternalUser = useIsAuthenticatedAsExternalUser();
  // Currently, we determine if the user should be served
  // these changes by checking user.type
  const risUser = isAuthenticatedAsExternalUser || data?.me?.type === ROLES.RIS;

  return {
    canAccessPriorsWithoutMRNMatch: !risUser,
    canAccessReporter: !risUser,
    canAccessSecondViewer: !risUser,
    canAccessViewerSettings: !risUser,
    canAccessWorklist: !risUser,
    canAccessViewer: true,
    canCreateAnnotations: !risUser,
    canCreateHangingProtocols: !risUser,
    canCreatePresentationStates: !risUser,
    canEditMouseSettings: !risUser,
    canEditViewerSettings: !risUser,
    canWriteKeyboardShortcuts: !risUser,
    shouldMoveUserDropdown: risUser,
    shouldShowSironaLogo: risUser,
    shouldRenderLeftSidebar: !risUser,
    shouldRenderUserDropdownInStudiesBar: risUser,
    shouldRenderShareButton: !risUser,
  };
};
